import React from 'react';
import Icon from '../../../components/AppIcon';

const relTime = (ts) => {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const SEVERITY = {
  critical: {
    icon: 'AlertCircle',
    iconColor: '#EF4444',
    iconBg: '#FEE2E2',
    badge: { color: '#EF4444', bg: '#FEE2E2' },
    border: 'border-red-100'
  },
  warning: {
    icon: 'AlertTriangle',
    iconColor: '#F59E0B',
    iconBg: '#FEF3C7',
    badge: { color: '#F59E0B', bg: '#FEF3C7' },
    border: 'border-amber-100'
  },
  info: {
    icon: 'Info',
    iconColor: '#3B82F6',
    iconBg: '#EFF6FF',
    badge: { color: '#3B82F6', bg: '#EFF6FF' },
    border: 'border-blue-100'
  }
};

const PLATFORM_LABELS = {
  wordpress: 'WordPress',
  pinterest: 'Pinterest',
  facebook: 'Facebook',
  instagram: 'Instagram',
  threads: 'Threads',
  etsy: 'Etsy',
  openai: 'OpenAI',
  midjourney: 'Midjourney',
  all: 'System'
};

const PLATFORM_RETRY_ROUTES = {
  wordpress: '/wp/posts',
  pinterest: '/pin/library',
  facebook: '/fb/page-stats',
  instagram: '/threads/create',
  threads: '/threads/create',
  etsy: '/etsy/accounts'
};

const ErrorLogFeed = ({ alerts = [], loading }) => {
  const critical = alerts.filter(a => a.severity === 'critical').length;
  const warning = alerts.filter(a => a.severity === 'warning').length;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm animate-pulse">
        <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between">
          <div className="h-4 bg-slate-100 rounded w-1/4" />
          <div className="h-4 bg-slate-100 rounded w-16" />
        </div>
        <div className="p-4 space-y-3">
          {[1, 2].map(i => <div key={i} className="h-16 bg-slate-100 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between flex-shrink-0">
        <h3 className="text-sm font-semibold text-[#0F172A]">Alerts</h3>
        <div className="flex items-center gap-2">
          {critical > 0 && (
            <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full">
              {critical} critical
            </span>
          )}
          {warning > 0 && (
            <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
              {warning} warning
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto" style={{ maxHeight: 320 }}>
        {alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <Icon name="ShieldCheck" size={20} className="text-emerald-500" />
            </div>
            <p className="text-sm font-medium text-emerald-600">Everything is running normally</p>
            <p className="text-xs text-[#94A3B8]">No active alerts</p>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {alerts.map(alert => {
              const cfg = SEVERITY[alert.severity] || SEVERITY.info;
              const retryRoute = PLATFORM_RETRY_ROUTES[alert.platform];

              return (
                <div key={alert.id} className={`rounded-xl border ${cfg.border} p-4`} style={{ backgroundColor: cfg.iconBg + '40' }}>
                  <div className="flex items-start gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: cfg.iconBg }}
                    >
                      <Icon name={alert.icon || cfg.icon} size={15} style={{ color: cfg.iconColor }} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-[#0F172A] leading-tight">{alert.title}</p>
                        <span className="text-[10px] text-[#94A3B8] flex-shrink-0 mt-0.5">{relTime(alert.timestamp)}</span>
                      </div>
                      <p className="text-xs text-[#64748B] leading-relaxed mb-2">{alert.message}</p>

                      <div className="flex items-center gap-2">
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                          style={{ color: cfg.iconColor, backgroundColor: cfg.iconBg }}
                        >
                          {PLATFORM_LABELS[alert.platform] || alert.platform}
                        </span>
                        {retryRoute && (
                          <a
                            href={retryRoute}
                            className="text-[10px] font-semibold text-[#64748B] hover:text-[#0F172A] underline transition-colors"
                          >
                            View Details
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorLogFeed;
